import { CALLSENSE_SCORE_FIELDS } from '@utazzo/shared';
import { Download, Pause, Play, Volume2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

import { getCallsenseRecordingObjectUrl } from '../callsense.api';
import { useCallsenseCall } from '../use-callsense';

import { formatCallDate, OUTCOME_LABELS, SCORE_LABELS } from './callsense-format';
import { CR_TOKENS_CSS } from './callsense-tokens';

/**
 * Call detail drawer - a faithful build of the CallSense 2.0 design: header
 * chips, waveform player, talk-split tiles, skill radar + 11 bars, extracted
 * trip details, coaching / red-flags / next-call boxes, and the diarized
 * chat-bubble transcript. Works for both CallerDesk and WhatsApp calls.
 */

const asArr = (v: unknown): string[] =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === 'string')
    : typeof v === 'string' && v
      ? [v]
      : [];

const mmss = (min: number): string =>
  `${Math.floor(min)}:${String(Math.round((min % 1) * 60)).padStart(2, '0')}`;
const secStr = (s: number): string =>
  `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

type Turn = { who: string; at: string; text: string };
const parseTurns = (t: unknown): Turn[] => {
  if (typeof t !== 'string') return [];
  const out: Turn[] = [];
  for (const line of t.split('\n')) {
    const m = line.match(/^\s*\[(\d+):(\d+)\]\s*(Agent|Customer)\s*:\s*(.*)$/i);
    if (m) out.push({ who: m[3] ?? '', at: `${m[1]}:${m[2]}`, text: m[4] ?? '' });
  }
  return out;
};

const radarPts = (vals: number[]): string => {
  const cx = 110;
  const cy = 95;
  const R = 78;
  return vals
    .map((v, i) => {
      const ang = -Math.PI / 2 + (i * 2 * Math.PI) / vals.length;
      const r = (R * v) / 5;
      return `${(cx + r * Math.cos(ang)).toFixed(1)},${(cy + r * Math.sin(ang)).toFixed(1)}`;
    })
    .join(' ');
};

const Tile = ({ k, v }: { k: string; v: React.ReactNode }): React.JSX.Element => (
  <div style={{ padding: 10, border: '1px solid var(--cr-bd)', borderRadius: 10 }}>
    <div style={{ fontSize: 12, color: 'var(--cr-mut)' }}>{k}</div>
    <b style={{ fontSize: 18 }}>{v}</b>
  </div>
);

const NoteBox = ({
  title,
  color,
  bg,
  items,
}: {
  title: string;
  color: string;
  bg: string;
  items: string[];
}): React.JSX.Element => (
  <div style={{ padding: 10, borderRadius: 10, background: bg }}>
    <div style={{ fontSize: 13, fontWeight: 800, color, marginBottom: 4 }}>{title}</div>
    <ul style={{ margin: 0, paddingLeft: 14, fontSize: 13, lineHeight: 1.45 }}>
      {(items.length ? items : ['None.']).map((x, i) => (
        <li key={i}>{x}</li>
      ))}
    </ul>
  </div>
);

export const CallDetailDrawer = ({
  callId,
  onClose,
  leadId,
}: {
  callId: string | null;
  onClose: () => void;
  leadId?: string;
}): React.JSX.Element | null => {
  const detail = useCallsenseCall(callId, leadId);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [curT, setCurT] = useState(0);
  const [durT, setDurT] = useState(0);
  const [vol, setVol] = useState(1);
  const [transcriptLang, setTranscriptLang] = useState<'english' | 'original'>('english');
  const [showFields, setShowFields] = useState(false);
  const [waUrl, setWaUrl] = useState<string | null>(null);
  const call = detail.data;
  // Reset the player whenever a different call opens.
  useEffect(() => {
    setPlaying(false);
    setCurT(0);
    setDurT(0);
  }, [callId]);
  // WhatsApp recordings 403 on a raw <audio src>, so fetch them through the
  // authenticated proxy as a blob and play that. CallerDesk plays directly.
  useEffect(() => {
    setWaUrl(null);
    if (!callId || call?.channel !== 'WHATSAPP' || !call?.recordingUrl) return undefined;
    let revoked = false;
    let url: string | null = null;
    void getCallsenseRecordingObjectUrl(callId)
      .then((u) => {
        if (revoked) URL.revokeObjectURL(u);
        else {
          url = u;
          setWaUrl(u);
        }
      })
      .catch(() => {});
    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [callId, call?.channel, call?.recordingUrl]);
  if (!callId) return null;

  const analysis = (call?.analysis ?? {}) as Record<string, unknown>;
  const isWa = call?.channel === 'WHATSAPP';
  // WhatsApp plays via the proxied blob; CallerDesk from its direct URL.
  const audioSrc = isWa ? waUrl : (call?.recordingUrl ?? null);
  const dur = call?.durationMinutes ?? 0;
  const aPct = call?.agentTalkPct ?? null;
  const aMin = aPct != null ? (dur * aPct) / 100 : null;
  const cMin = aPct != null ? dur - (aMin ?? 0) : null;
  const score5 = call?.overallScore != null ? (call.overallScore / 20).toFixed(1) : '-';
  const sentTone: Record<string, [string, string]> = {
    positive: ['var(--cr-okbg)', 'var(--cr-ok)'],
    neutral: ['var(--cr-sub2)', 'var(--cr-mut)'],
    negative: ['var(--cr-badbg)', 'var(--cr-bad)'],
  };
  const st: [string, string] = sentTone[call?.sentimentCustomer ?? 'neutral'] ?? [
    'var(--cr-sub2)',
    'var(--cr-mut)',
  ];
  const booked = call?.callOutcome === 'booked';

  const rationale = (analysis.score_rationale ?? {}) as Record<string, unknown>;
  const skills = CALLSENSE_SCORE_FIELDS.map((f) => {
    const v = typeof analysis[f] === 'number' ? (analysis[f] as number) : 0;
    return {
      label: SCORE_LABELS[f] ?? f,
      v,
      why: typeof rationale[f] === 'string' ? (rationale[f] as string) : '',
      color: v >= 4 ? 'var(--cr-ok)' : v >= 3 ? 'var(--cr-warn)' : 'var(--cr-bad)',
    };
  });
  const hasOriginal =
    typeof analysis.transcript_original === 'string' && analysis.transcript_original;
  const turns = parseTurns(
    transcriptLang === 'original' && hasOriginal
      ? analysis.transcript_original
      : analysis.transcript_english,
  );
  // Flat view of the extracted fields for the "all fields" expander.
  const allFields = Object.entries(analysis)
    .filter(([k]) => !['transcript_english', 'transcript_original', 'score_rationale'].includes(k))
    .map(
      ([k, v]) => [k, typeof v === 'object' ? JSON.stringify(v) : String(v)] as [string, string],
    );

  const trip: { k: string; v: string }[] = [
    { k: 'Destination', v: String(analysis.destination ?? call?.destination ?? '-') },
    { k: 'Travel dates', v: String(analysis.travel_dates ?? '-') },
    { k: 'Travellers', v: String(analysis.num_travellers ?? '-') },
    { k: 'Budget', v: String(analysis.budget ?? '-') },
    { k: 'Quoted price', v: String(analysis.quoted_price ?? '-') },
    { k: 'Language', v: String(call?.primaryLanguage ?? analysis.primary_language ?? '-') },
  ];

  return (
    <div className="cr-scope">
      <style>{CR_TOKENS_CSS}</style>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 50 }}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: 560,
          maxWidth: '100vw',
          background: 'var(--cr-card)',
          color: 'var(--cr-tx)',
          zIndex: 51,
          boxShadow: '-20px 0 50px -30px rgba(0,0,0,.5)',
          display: 'flex',
          flexDirection: 'column',
          borderLeft: '1px solid var(--cr-bd)',
          fontFamily: 'inherit',
        }}
      >
        {/* header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 10,
            padding: '16px 18px',
            borderBottom: '1px solid var(--cr-bd2)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: 700,
                  borderRadius: 6,
                  padding: '2px 7px',
                  background: isWa ? 'var(--cr-okbg)' : 'var(--cr-accbg)',
                  color: isWa ? 'var(--cr-wa)' : 'var(--cr-acc)',
                }}
              >
                {isWa ? 'WhatsApp' : 'CallerDesk'}
              </span>
              <span style={{ fontSize: 13, color: 'var(--cr-mut)' }}>
                {call?.direction ?? '-'} · {formatCallDate(call?.callDate ?? null)} · {mmss(dur)}{' '}
                min · {call?.primaryLanguage ?? '-'}
              </span>
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-.02em' }}>
              {String(analysis.call_reason ?? 'Call')}{' '}
              <span style={{ color: 'var(--cr-mut)', fontWeight: 600 }}>with</span>{' '}
              {call?.agentName ?? '-'}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', fontSize: 13 }}>
              <span style={{ borderRadius: 6, padding: '2px 8px', background: 'var(--cr-sub2)' }}>
                Outcome:{' '}
                <b>{OUTCOME_LABELS[call?.callOutcome ?? ''] ?? call?.callOutcome ?? '-'}</b>
              </span>
              <span
                style={{
                  borderRadius: 6,
                  padding: '2px 8px',
                  background: st[0],
                  color: st[1],
                  fontWeight: 700,
                }}
              >
                {call?.sentimentCustomer ?? 'n/a'}
              </span>
              <span
                style={{
                  borderRadius: 6,
                  padding: '2px 8px',
                  background: booked ? 'var(--cr-okbg)' : 'var(--cr-sub2)',
                  color: booked ? 'var(--cr-ok)' : 'var(--cr-mut)',
                  fontWeight: 700,
                }}
              >
                {booked ? 'Booking confirmed' : 'Not booked yet'}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 28,
              height: 28,
              border: '1px solid var(--cr-bd)',
              borderRadius: 8,
              background: 'var(--cr-card)',
              color: 'var(--cr-tx)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {detail.isLoading ? (
          <div style={{ padding: 24, color: 'var(--cr-mut)', fontSize: 15 }}>Loading analysis…</div>
        ) : (
          <div
            style={{
              flex: 1,
              overflow: 'auto',
              padding: '16px 18px',
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            {/* player (functional: play / pause / seek / volume / download) */}
            {(() => {
              const playTotal = durT || dur * 60;
              const frac = playTotal ? curT / playTotal : 0;
              const togglePlay = (): void => {
                const el = audioRef.current;
                if (!el) return;
                if (playing) {
                  el.pause();
                  setPlaying(false);
                } else {
                  void el.play();
                  setPlaying(true);
                }
              };
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    border: '1px solid var(--cr-bd)',
                    borderRadius: 12,
                    background: 'var(--cr-sub)',
                  }}
                >
                  <button
                    type="button"
                    onClick={togglePlay}
                    disabled={!audioSrc}
                    aria-label={playing ? 'Pause' : 'Play'}
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: '50%',
                      border: 0,
                      background: 'var(--cr-acc)',
                      color: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      cursor: audioSrc ? 'pointer' : 'default',
                      opacity: audioSrc ? 1 : 0.4,
                    }}
                  >
                    {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
                  </button>
                  <span
                    style={{
                      fontSize: 13,
                      color: 'var(--cr-mut)',
                      whiteSpace: 'nowrap',
                      width: 34,
                      textAlign: 'right',
                    }}
                    className="tabular-nums"
                  >
                    {secStr(curT)}
                  </span>
                  <svg
                    viewBox="0 0 300 36"
                    preserveAspectRatio="none"
                    onClick={(e) => {
                      const el = audioRef.current;
                      if (!el || !playTotal) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const t = ((e.clientX - rect.left) / rect.width) * playTotal;
                      el.currentTime = t;
                      setCurT(t);
                    }}
                    style={{
                      flex: 1,
                      height: 36,
                      display: 'block',
                      cursor: audioSrc ? 'pointer' : 'default',
                    }}
                  >
                    {Array.from({ length: 60 }, (_, i) => {
                      const h = 6 + ((i * 37) % 26);
                      const played = i / 60 <= frac;
                      return (
                        <rect
                          key={i}
                          x={i * 5}
                          y={18 - h / 2}
                          width={3}
                          height={h}
                          rx={1.5}
                          fill={played ? 'var(--cr-acc)' : 'var(--cr-sub2)'}
                        />
                      );
                    })}
                  </svg>
                  <span
                    style={{ fontSize: 13, color: 'var(--cr-mut)', whiteSpace: 'nowrap' }}
                    className="tabular-nums"
                  >
                    {secStr(playTotal)}
                  </span>
                  <Volume2 className="h-4 w-4" style={{ color: 'var(--cr-mut)', flexShrink: 0 }} />
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={vol}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setVol(v);
                      if (audioRef.current) audioRef.current.volume = v;
                    }}
                    style={{ width: 56, flexShrink: 0, accentColor: 'var(--cr-acc)' }}
                    aria-label="Volume"
                  />
                  {audioSrc && (
                    <a
                      href={audioSrc}
                      download
                      title="Download recording"
                      style={{ color: 'var(--cr-mut)', display: 'flex', flexShrink: 0 }}
                    >
                      <Download className="h-4 w-4" />
                    </a>
                  )}
                  {audioSrc && (
                    <audio
                      ref={audioRef}
                      src={audioSrc}
                      preload="metadata"
                      onLoadedMetadata={(e) => setDurT(e.currentTarget.duration)}
                      onTimeUpdate={(e) => setCurT(e.currentTarget.currentTime)}
                      onEnded={() => setPlaying(false)}
                      style={{ display: 'none' }}
                    />
                  )}
                </div>
              );
            })()}

            {/* AI summary */}
            {call?.summary && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 14, fontWeight: 800 }}>AI summary</div>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5, color: 'var(--cr-tx)' }}>
                  {call.summary}
                </p>
              </div>
            )}

            {/* talk split */}
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 8 }}
            >
              <Tile
                k="Agent talk"
                v={
                  <>
                    {aMin != null ? mmss(aMin) : '-'}{' '}
                    <span style={{ fontSize: 13, color: 'var(--cr-acc)' }}>
                      {aPct != null ? `${aPct}%` : ''}
                    </span>
                  </>
                }
              />
              <Tile
                k="Customer talk"
                v={
                  <>
                    {cMin != null ? mmss(cMin) : '-'}{' '}
                    <span style={{ fontSize: 13, color: 'var(--cr-mut)' }}>
                      {aPct != null ? `${100 - aPct}%` : ''}
                    </span>
                  </>
                }
              />
              <Tile
                k="Overtalk"
                v={
                  typeof analysis.interruptions_count === 'number'
                    ? analysis.interruptions_count
                    : '-'
                }
              />
              <Tile k="Confidence" v={String(analysis.transcription_confidence ?? '-')} />
            </div>

            {/* scores */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '150px minmax(0,1fr)',
                gap: 14,
                alignItems: 'start',
              }}
            >
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 4 }}>
                  Quality <span style={{ color: 'var(--cr-acc)' }}>{score5}</span> / 5
                </div>
                <svg
                  viewBox="0 0 220 200"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                >
                  {[1, 3, 5].map((v) => (
                    <polygon
                      key={v}
                      points={radarPts(Array(11).fill(v))}
                      fill="none"
                      stroke="var(--cr-bd)"
                    />
                  ))}
                  <polygon
                    points={radarPts(skills.map((s) => s.v))}
                    fill="var(--cr-acc)"
                    fillOpacity={0.25}
                    stroke="var(--cr-acc)"
                    strokeWidth={1.5}
                  />
                </svg>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {skills.map((s, i) => (
                  <div
                    key={i}
                    title={s.why || undefined}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '118px minmax(0,1fr) 20px',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 13,
                      cursor: s.why ? 'help' : undefined,
                    }}
                  >
                    <span
                      style={{
                        color: 'var(--cr-mut)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {s.label}
                    </span>
                    <div style={{ height: 6, borderRadius: 3, background: 'var(--cr-sub2)' }}>
                      <div
                        style={{
                          height: '100%',
                          width: `${(s.v / 5) * 100}%`,
                          background: s.color,
                          borderRadius: 3,
                        }}
                      />
                    </div>
                    <b style={{ textAlign: 'right' }}>{s.v ? s.v.toFixed(1) : '-'}</b>
                  </div>
                ))}
              </div>
            </div>

            {/* trip */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 14, fontWeight: 800 }}>Extracted trip details</div>
              <div
                style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 6 }}
              >
                {trip.map((t, i) => (
                  <div
                    key={i}
                    style={{ padding: '8px 10px', borderRadius: 8, background: 'var(--cr-sub)' }}
                  >
                    <div style={{ fontSize: 12, color: 'var(--cr-mut)' }}>{t.k}</div>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{t.v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* coaching / flags / next */}
            <div
              style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8 }}
            >
              <NoteBox
                title="Coaching notes"
                color="var(--cr-ok)"
                bg="var(--cr-okbg)"
                items={asArr(analysis.coaching_notes)}
              />
              <NoteBox
                title={`Red flags (${asArr(analysis.red_flags).length})`}
                color="var(--cr-bad)"
                bg="var(--cr-badbg)"
                items={asArr(analysis.red_flags)}
              />
              <NoteBox
                title="Next-call focus"
                color="var(--cr-acc)"
                bg="var(--cr-accbg)"
                items={asArr(analysis.next_call_focus ?? analysis.follow_up_actions)}
              />
            </div>

            {/* transcript */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 800,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  Transcript
                  {hasOriginal && (
                    <span
                      style={{
                        display: 'inline-flex',
                        gap: 2,
                        background: 'var(--cr-sub2)',
                        borderRadius: 8,
                        padding: 2,
                      }}
                    >
                      {(['english', 'original'] as const).map((l) => (
                        <button
                          key={l}
                          type="button"
                          onClick={() => setTranscriptLang(l)}
                          style={{
                            border: 0,
                            borderRadius: 6,
                            padding: '2px 8px',
                            fontSize: 12.5,
                            fontWeight: 700,
                            cursor: 'pointer',
                            background: transcriptLang === l ? 'var(--cr-card)' : 'transparent',
                            color: transcriptLang === l ? 'var(--cr-tx)' : 'var(--cr-mut)',
                          }}
                        >
                          {l === 'english' ? 'English' : (call?.primaryLanguage ?? 'Original')}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
                {aMin != null && cMin != null && (
                  <span style={{ fontSize: 13, color: 'var(--cr-mut)' }}>
                    agent {mmss(aMin)} · customer {mmss(cMin)}
                  </span>
                )}
              </div>
              {turns.length ? (
                turns.map((t, i) => {
                  const agent = t.who.toLowerCase() === 'agent';
                  return (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        alignItems: agent ? 'flex-start' : 'flex-end',
                      }}
                    >
                      <span style={{ fontSize: 12, color: 'var(--cr-mut)' }}>
                        {t.who} · {t.at}
                      </span>
                      <div
                        style={{
                          maxWidth: '86%',
                          padding: '8px 11px',
                          borderRadius: 12,
                          fontSize: 14,
                          lineHeight: 1.45,
                          background: agent ? 'var(--cr-accbg)' : 'var(--cr-sub2)',
                          color: 'var(--cr-tx)',
                        }}
                      >
                        {t.text}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ fontSize: 14, color: 'var(--cr-mut)' }}>
                  No diarized transcript available.
                </div>
              )}
            </div>

            {call?.destination && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px 12px',
                  border: '1px solid var(--cr-bd)',
                  borderRadius: 10,
                  fontSize: 14,
                }}
              >
                <span style={{ color: 'var(--cr-mut)' }}>Destination</span>
                <b>{call.destination}</b>
              </div>
            )}

            {/* all extracted fields */}
            {allFields.length > 0 && (
              <div style={{ border: '1px solid var(--cr-bd2)', borderRadius: 10 }}>
                <button
                  type="button"
                  onClick={() => setShowFields((v) => !v)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    border: 0,
                    background: 'transparent',
                    color: 'var(--cr-tx)',
                    padding: '10px 12px',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  All analysis fields
                  <span style={{ color: 'var(--cr-mut)' }}>{showFields ? '▲' : '▼'}</span>
                </button>
                {showFields && (
                  <div
                    style={{
                      borderTop: '1px solid var(--cr-bd2)',
                      padding: '8px 12px',
                      display: 'grid',
                      gridTemplateColumns: '150px minmax(0,1fr)',
                      gap: '4px 10px',
                      fontSize: 13,
                    }}
                  >
                    {allFields.map(([k, v]) => (
                      <React.Fragment key={k}>
                        <span style={{ color: 'var(--cr-mut)', wordBreak: 'break-word' }}>{k}</span>
                        <span style={{ wordBreak: 'break-word' }}>{v}</span>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
